import { Injectable } from '@angular/core';
import { ParamMap } from '@angular/router';
import { PortalTypeEnum } from '@ats/api/cm';
import { Observable } from 'rxjs';
import { CmInterceptHeaders, INGRESS_TYPE } from '../models';
import { CmAuthService } from './auth.service';
import { CmSettingsService } from './settings.service';

@Injectable({
	providedIn: 'root',
})
export class CmSSOService {
	constructor(
		private auth: CmAuthService,
		private settings: CmSettingsService
	) {}

	/**
	 * SSO ingress to the candidate portal
	 * @method
	 * @param {INGRESS_TYPE} ingressType - The provider/ingress type
	 * @param {ParamMap} queryParamMap - The query params from the request
	 * @param {boolean} [internal=false] - Flag indicating if this an internal user
	 * @return {Observable<boolean>} - Emits true if the operation was a success
	 */
	fromSSO(
		ingressType: INGRESS_TYPE,
		queryParamMap: ParamMap,
		internal: boolean = false
	): Observable<boolean> {
		return this.sso(ingressType, queryParamMap, internal);
	}

	encodeQueryParams(queryParamMap: ParamMap): string {
		return queryParamMap.keys
			.map(key => `${key}=${encodeURI(queryParamMap.get(key))}`)
			.join('&');
	}

	/**
	 * SSO ingress
	 * @method
	 * @private
	 * @param {string} provider - The provider for the SSO request
	 * @param {ParamMap} queryParamMap - The query params from the request
	 * @param {boolean} internal - Flag indicating if this an internal user
	 * @return {Observable<boolean>} - Emits true if the operation was a success
	 */
	private sso(provider: string, queryParamMap: ParamMap, internal: boolean): Observable<boolean> {
		const queryString = this.encodeQueryParams(queryParamMap);
		const ssoPath = this.getSSOUrlBase(internal) + `${provider}?${queryString}`;
		return this.auth.updateTokenWithGet$(ssoPath, {
			headers: { ...CmInterceptHeaders.BypassAuth, ...CmInterceptHeaders.IgnoreAuthError },
			withCredentials: true,
		});
	}

	/**
	 * Determine SSO ingress path for all portals
	 * @method
	 * @private
	 * @param {boolean} internal - Flag indicating if this an internal user
	 * @return {string} - Emits the base path for SSO ingress
	 */
	private getSSOUrlBase(internal: boolean): string {
		let baseRoute = `${this.settings.api}/${this.settings.client}`;
		if (this.settings.portal !== PortalTypeEnum.CandidatePortal) {
			baseRoute += internal ? '/internal' : '/sponsor';
		}
		return `${baseRoute}/sso/ingress/`;
	}
}
